// react
import React, { useState, useEffect } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Grid, Typography, Tooltip, IconButton, TextField, Box, useMediaQuery, SwipeableDrawer, useTheme } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon, DeleteForever as DeleteForeverIcon, Add as AddIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// ui-components
import { Puller } from "ui-components/Puller";

// A ---------------------------------------------------------------------- M

const SearchDialog = ({
  open,
  onClose,
  onSearch,
  defaultAttributesList = [
    { key: "documentNames", label: "metadata.documentnames", placeholder: "metadata.documentnames_placeholder" },
    { key: "soggettiFullName", label: "metadata.subjectfullname", placeholder: "metadata.subjectfullname_placeholder" },
    { key: "soggettiCodiceFiscale", label: "metadata.subjecttaxcode", placeholder: "metadata.subjecttaxcode_placeholder" },
    { key: "soggettiOrganizzazione", label: "metadata.subjectorganization", placeholder: "metadata.subjectorganization_placeholder" },
  ],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [criteria, setCriteria] = useState({
    startDate: null,
    endDate: null,
    defaultAttributes: {},
    customAttributes: [],
  });

  useEffect(() => {
    const initDefaults = {};
    defaultAttributesList.forEach((attr) => {
      initDefaults[attr.key] = "";
    });
    setCriteria((prev) => ({ ...prev, defaultAttributes: initDefaults }));
  }, []);

  const handleChange = (name) => (eventOrValue) => {
    const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    setCriteria((prev) => ({ ...prev, [name]: value }));
  };

  const handleDefaultAttributeChange = (key) => (event) => {
    const value = event.target.value;
    setCriteria((prev) => ({ ...prev, defaultAttributes: { ...prev.defaultAttributes, [key]: value } }));
  };

  const handleCustomAttributeChange = (index, field) => (event) => {
    const value = event.target.value;
    setCriteria((prev) => {
      const updated = [...prev.customAttributes];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, customAttributes: updated };
    });
  };

  const addCustomAttribute = () => {
    setCriteria((prev) => ({
      ...prev,
      customAttributes: [...prev.customAttributes, { key: "", value: "" }],
    }));
  };

  const removeCustomAttribute = (index) => {
    setCriteria((prev) => {
      const updated = prev.customAttributes.filter((_, i) => i !== index);
      return { ...prev, customAttributes: updated };
    });
  };

  const handleSearch = () => {
    const unixCriteria = {
      startDate: criteria.startDate ? Math.floor(new Date(criteria.startDate).getTime() / 1000).toString() : null,
      endDate: criteria.endDate ? Math.floor(new Date(criteria.endDate).getTime() / 1000).toString() : null,
    };

    const trimmedDefaults = Object.fromEntries(Object.entries(criteria.defaultAttributes).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]));

    const trimmedCustomAttributes = criteria.customAttributes.reduce((acc, attr) => {
      if (attr.key) {
        acc[attr.key.trim()] = typeof attr.value === "string" ? attr.value.trim() : attr.value;
      }
      return acc;
    }, {});

    const finalCriteria = {
      ...unixCriteria,
      ...trimmedDefaults,
      ...trimmedCustomAttributes,
    };

    onSearch(finalCriteria);
    onClose();
  };

  const FormContent = (
    <Grid container spacing={2}>
      <Grid item xs={12} mt={1}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label={t("start_upload")}
            value={criteria.startDate}
            onChange={handleChange("startDate")}
            renderInput={(params) => <TextField {...params} />}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                backgroundColor: "#ffffff",
              },
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label={t("end_upload")}
            value={criteria.endDate}
            onChange={handleChange("endDate")}
            renderInput={(params) => <TextField {...params} />}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                backgroundColor: "#ffffff",
              },
            }}
          />
        </LocalizationProvider>
      </Grid>

      {defaultAttributesList.map((attr) => (
        <Grid item xs={12} key={attr.key}>
          <TextField
            fullWidth
            margin="dense"
            label={t(attr.label)}
            placeholder={attr.placeholder ? t(attr.placeholder) : ""}
            value={criteria.defaultAttributes[attr.key] || ""}
            onChange={handleDefaultAttributeChange(attr.key)}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                backgroundColor: "#ffffff",
              },
            }}
          />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary">
          {t("custom_attributes_hint")}
        </Typography>
      </Grid>

      {criteria.customAttributes.map((attr, index) => (
        <Grid item container spacing={2} key={index} alignItems="center">
          <Grid item xs={12} sm={5.5}>
            <TextField
              label={t("key")}
              fullWidth
              margin="dense"
              value={attr.key}
              onChange={handleCustomAttributeChange(index, "key")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={5.5}>
            <TextField
              label={t("value")}
              fullWidth
              margin="dense"
              value={attr.value}
              onChange={handleCustomAttributeChange(index, "value")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={1}>
            <Tooltip title={t("remove")} placement="top">
              <IconButton color="error" onClick={() => removeCustomAttribute(index)}>
                <DeleteForeverIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Box>
          <Button
            variant="contained"
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              py: 1,
            }}
            onClick={addCustomAttribute}
            startIcon={<AddIcon />}
          >
            {t("parameter")}
          </Button>
        </Box>
      </Grid>
    </Grid>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "90%",
            borderTopLeftRadius: "4%",
            borderTopRightRadius: "4%",
          },
        }}
      >
        <Puller />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
            {t("search")}
          </Typography>

          {FormContent}

          <Grid container spacing={2} mt={5}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                }}
                onClick={handleSearch}
              >
                {t("search")}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="text"
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                }}
                onClick={onClose}
              >
                {t("cancel")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              {t("search")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("close")} placement="top">
              <IconButton onClick={onClose} color="error" edge="end">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>{FormContent}</DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={onClose}
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleSearch}
        >
          {t("search")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SearchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  defaultAttributesList: PropTypes.array,
};

export default SearchDialog;
